import { jsPDF } from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  AMBER, INK, MUTED, MARGIN, TABLE_STYLES, hexToRgb,
  pageSize, hasBranding, drawPageTitle, drawFooters,
} from "./pdfExport";
import {
  MARKER_CATEGORY, getRoomEffect, directionColor,
} from "./vastuReportData";
import { evaluateMarker, STATUS_COLORS } from "./rooms";
import { renderSnapshot } from "./snapshot";
import { buildSectors, computeBBox } from "./sectors";

const PURUSHA_SRC = (process.env.PUBLIC_URL || "") + "/assets/vastu-purusha-outline.png";
const STATUS_LABEL = { ideal: "Ideal", ok: "Acceptable", conflict: "Conflict", unknown: "—" };

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

function drawReportCover(doc, { branding, title }) {
  const { w, h } = pageSize(doc);
  const branded = hasBranding(branding);
  const cx = w / 2;
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, w, h, "F");
  doc.setFillColor(...AMBER);
  doc.rect(0, 0, w, 4, "F");
  doc.rect(0, h - 4, w, 4, "F");

  let y = h * 0.24;
  if (branding?.logo_data_url) {
    try {
      const props = doc.getImageProperties(branding.logo_data_url);
      const logoH = 26;
      const logoW = Math.min(70, (props.width / props.height) * logoH);
      doc.addImage(branding.logo_data_url, props.fileType || "PNG", cx - logoW / 2, y - logoH, logoW, logoH);
    } catch (e) { /* ignore */ }
  }

  y = h * 0.37;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...AMBER);
  doc.text("VASTU GUIDANCE REPORT", cx, y, { align: "center", charSpace: 1.5 });

  y += 16;
  doc.setFontSize(27);
  doc.setTextColor(245, 245, 245);
  doc.text("Room Placement & Vastu Purusha", cx, y, { align: "center", maxWidth: w - MARGIN * 3 });

  y += 14;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(203, 213, 225);
  doc.text(title, cx, y, { align: "center" });

  y += 10;
  doc.setDrawColor(...AMBER); doc.setLineWidth(0.6);
  doc.line(cx - 18, y, cx + 18, y); doc.setLineWidth(0.2);

  y += 12;
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text("Vastu Purusha on the plan · 16-direction dial · room placement analysis", cx, y, { align: "center", maxWidth: w - MARGIN * 3 });

  if (branded) {
    const contact = [
      branding.consultant_name && branding.firm ? branding.consultant_name : (branding.firm || branding.consultant_name),
      [branding.phone, branding.email].filter(Boolean).join("   ·   "),
      branding.website,
    ].filter(Boolean);
    let cyy = h - 22;
    contact.forEach((line, i) => {
      doc.setFont("helvetica", i === 0 ? "bold" : "normal");
      doc.setFontSize(i === 0 ? 10 : 8);
      doc.setTextColor(...(i === 0 ? [226, 232, 240] : [148, 163, 184]));
      doc.text(String(line), cx, cyy, { align: "center" });
      cyy += i === 0 ? 5 : 4;
    });
  } else {
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    doc.text("Vastu Mandala · Floor Plan Sector Analyzer", cx, h - 12, { align: "center" });
  }
  doc.setDrawColor(226, 232, 240);
}

export function loadPurushaImage() {
  return loadImage(PURUSHA_SRC);
}

export function drawPlanOverlayPage(doc, { branding, title, plan, purushaImg, pageFormat = "a4", orient = "portrait" }) {
  doc.addPage(pageFormat, orient);
  drawPageTitle(doc, "Vastu Purusha over the Plan", title, branding);
  const { w, h } = pageSize(doc);
  const colW = w - MARGIN * 2;

  let y = 26;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...INK);
  const intro = "The Vastu Purusha is shown reclining over your floor plan with his head toward the North-East and his feet toward the South-West, aligned to your True North setting. The 16-direction dial around the plan marks each direction and its degree range so every zone can be read at a glance.";
  const lines = doc.splitTextToSize(intro, colW);
  doc.text(lines, MARGIN, y);
  y += lines.length * 5 + 5;

  const { image, imageDims, polygon, center, rotation, markers } = plan;
  const hasPlan = image && polygon && polygon.length >= 3 && center;
  const boxH = h - y - 20;
  if (hasPlan) {
    const bbox = computeBBox(polygon, imageDims?.width || 800, imageDims?.height || 600);
    const sectors = buildSectors(16, center, polygon, bbox, rotation);
    const canvas = renderSnapshot({
      image, imageDims, rotation, polygon, sectors, center,
      sectorCount: 16, showOverlay: true, showDegrees: true, markers, purushaImg,
    });
    const ratio = Math.min(colW / canvas.width, boxH / canvas.height);
    const iw = canvas.width * ratio, ih = canvas.height * ratio;
    const ix = (w - iw) / 2;
    doc.addImage(canvas.toDataURL("image/jpeg", 0.92), "JPEG", ix, y, iw, ih);
    doc.setDrawColor(203, 213, 225);
    doc.rect(ix, y, iw, ih);
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text("Vastu Purusha overlaid on the plan with the 16-direction dial — head to the North-East, feet to the South-West.", w / 2, y + ih + 4, { align: "center" });
  } else {
    if (purushaImg) {
      const ratio = Math.min(colW / purushaImg.naturalWidth, boxH / purushaImg.naturalHeight);
      const iw = purushaImg.naturalWidth * ratio, ih = purushaImg.naturalHeight * ratio;
      doc.addImage(purushaImg, "PNG", (w - iw) / 2, y, iw, ih);
    }
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text("Draw the house polygon on a floor plan to see the Vastu Purusha and the 16-direction dial over your plan.", w / 2, h - 16, { align: "center" });
  }
}

// Page: the uploaded floor plan on its own, with no markings, fitted to the page.
export function drawRawImagePage(doc, { branding, title, image, imageDims, pageFormat = "a4", orient = "portrait" }) {
  if (!image) return;
  doc.addPage(pageFormat, orient);
  drawPageTitle(doc, "Floor Plan", title, branding);
  const { w, h } = pageSize(doc);
  const y = 24;
  const boxW = w - MARGIN * 2, boxH = h - y - 16;
  const iw0 = imageDims?.width || image.naturalWidth;
  const ih0 = imageDims?.height || image.naturalHeight;
  const ratio = Math.min(boxW / iw0, boxH / ih0);
  const iw = iw0 * ratio, ih = ih0 * ratio;
  const x = (w - iw) / 2, iy = y + (boxH - ih) / 2;
  const c = document.createElement("canvas");
  c.width = iw0; c.height = ih0;
  c.getContext("2d").drawImage(image, 0, 0, iw0, ih0);
  doc.addImage(c.toDataURL("image/jpeg", 0.92), "JPEG", x, iy, iw, ih);
  doc.setDrawColor(203, 213, 225);
  doc.rect(x, iy, iw, ih);
}

export function drawPlacementAnalysis(doc, { branding, title, markers, center, rotation, pageFormat = "a4" }) {
  doc.addPage(pageFormat, "portrait");
  drawPageTitle(doc, "Room Placement Analysis", title, branding);
  const { w } = pageSize(doc);

  let y = 25;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...MUTED);
  const note = markers.length
    ? "Based on the rooms placed on your plan. Each entry shows the room, the direction it falls in, and the Vastu effect of that specific placement."
    : "No rooms were placed on the plan. Add room markers to generate a personalised placement analysis.";
  const lines = doc.splitTextToSize(note, w - MARGIN * 2);
  doc.text(lines, MARGIN, y);
  y += lines.length * 4.6 + 4;
  if (!markers.length) return;

  const rows = markers.map((m, i) => {
    const ev = evaluateMarker(m, center, rotation);
    const cat = MARKER_CATEGORY[m.type];
    const dir = ev.direction;
    const specific = cat && dir ? getRoomEffect(cat, dir) : null;
    const analysis = specific || (ev.info ? ev.info.guidance : "—");
    const dirLabel = ev.info ? `${dir} · ${ev.info.name}` : dir || "—";
    const element = ev.info ? ev.info.element : "—";
    return { i: i + 1, room: ev.room.label, dir: dirLabel, dirCode: dir, element, analysis, status: ev.status };
  });

  autoTable(doc, {
    ...TABLE_STYLES,
    startY: y,
    margin: { left: MARGIN, right: MARGIN },
    head: [["#", "Room", "Direction", "Element", "Vastu effect of this placement", "Status"]],
    body: rows.map((r) => [r.i, r.room, r.dir, r.element, r.analysis, STATUS_LABEL[r.status]]),
    columnStyles: {
      0: { cellWidth: 7, halign: "center" },
      1: { cellWidth: 26, fontStyle: "bold" },
      2: { cellWidth: 24 },
      3: { cellWidth: 16 },
      4: { cellWidth: "auto" },
      5: { cellWidth: 20, halign: "center" },
    },
    didParseCell: (d) => {
      if (d.section !== "body") return;
      const r = rows[d.row.index];
      if (!r) return;
      if (d.column.index === 2 && r.dirCode) {
        d.cell.styles.textColor = hexToRgb(directionColor(r.dirCode));
        d.cell.styles.fontStyle = "bold";
      }
      if (d.column.index === 5) {
        d.cell.styles.textColor = hexToRgb(STATUS_COLORS[r.status] || "#64748B");
        d.cell.styles.fontStyle = "bold";
      }
    },
  });
}

// Build the branded "Vastu Guidance" PDF: cover → plan with Purusha overlay + 16-direction dial → marker-driven room placement analysis.
export async function buildPlacementDevtaPDF({
  planName, branding = null, pageFormat = "a4",
  image = null, imageDims = null, polygon = [], center = null, rotation = 0, markers = [],
}) {
  const title = planName || "Untitled plan";
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: pageFormat });
  const purushaImg = await loadImage(PURUSHA_SRC);

  drawReportCover(doc, { branding, title });
  drawPlanOverlayPage(doc, { branding, title, plan: { image, imageDims, polygon, center, rotation, markers }, purushaImg });
  drawPlacementAnalysis(doc, { branding, title, markers, center, rotation });

  drawFooters(doc, branding);
  return doc;
}
